import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Monitor, ExternalLink } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface DisplayPairingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  businessId: string;
  onPaired: () => void;
}

export function DisplayPairingDialog({
  open,
  onOpenChange,
  businessId,
  onPaired,
}: DisplayPairingDialogProps) {
  const [step, setStep] = useState<'info' | 'code'>('info');
  const [pairingCode, setPairingCode] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [orientation, setOrientation] = useState<'landscape' | 'portrait'>('landscape');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const playerUrl = `${window.location.origin}/signage/player`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!pairingCode.trim()) {
      setError('Please enter the pairing code');
      return;
    }

    if (!displayName.trim()) {
      setError('Please enter a display name');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke('signage-complete-pairing', {
        body: {
          pairing_code: pairingCode.trim(),
          business_id: businessId,
          display_name: displayName.trim(),
          orientation,
        },
      });

      if (fnError) throw fnError;

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to pair display');
      }

      toast.success('Display paired successfully!', {
        description: `${displayName} is now connected`,
      });

      onPaired();
      handleClose();
    } catch (err: any) {
      console.error('Pairing error:', err);
      setError(err.message || 'Failed to pair display');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setStep('info');
    setPairingCode('');
    setDisplayName('');
    setOrientation('landscape');
    setError(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Monitor className="w-5 h-5" />
            Pair New Display
          </DialogTitle>
          <DialogDescription>
            {step === 'info'
              ? 'Follow these steps to connect a new TV or display'
              : 'Enter the pairing code shown on your display'}
          </DialogDescription>
        </DialogHeader>

        {step === 'info' ? (
          <>
            <div className="space-y-4 py-4">
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm flex items-center justify-center">
                    1
                  </div>
                  <div>
                    <p className="font-medium">Open the player URL on your display</p>
                    <div className="mt-2 flex items-center gap-2">
                      <Input
                        value={playerUrl}
                        readOnly
                        className="text-sm bg-muted"
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => {
                          navigator.clipboard.writeText(playerUrl);
                          toast.success('URL copied!');
                        }}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm flex items-center justify-center">
                    2
                  </div>
                  <div>
                    <p className="font-medium">Wait for the pairing code</p>
                    <p className="text-sm text-muted-foreground">
                      A 6-digit code will appear on the display screen
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm flex items-center justify-center">
                    3
                  </div>
                  <div>
                    <p className="font-medium">Enter the code here</p>
                    <p className="text-sm text-muted-foreground">
                      Click "Continue" below and enter the code
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={() => setStep('code')}>
                Continue
              </Button>
            </DialogFooter>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              {error && (
                <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="pairing-code">Pairing Code</Label>
                <Input
                  id="pairing-code"
                  placeholder="Enter 6-digit code"
                  value={pairingCode}
                  onChange={(e) => setPairingCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                  className="text-center text-2xl tracking-widest font-mono"
                  autoFocus
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="display-name">Display Name</Label>
                <Input
                  id="display-name"
                  placeholder="e.g., Front Lobby TV"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="orientation">Orientation</Label>
                <Select value={orientation} onValueChange={(v) => setOrientation(v as any)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="landscape">Landscape (horizontal)</SelectItem>
                    <SelectItem value="portrait">Portrait (vertical)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setStep('info')}>
                Back
              </Button>
              <Button type="submit" disabled={isLoading || pairingCode.length !== 6}>
                {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Pair Display
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
